import { Box, IconButton } from "@mui/material";
import React, { useState, useMemo, useEffect, useRef } from "react";
import DiaryHeader from "./DiaryComponents/DiaryHeader";
import DayCard from "./DiaryComponents/DayCard";
import { useSelector, useDispatch } from "react-redux";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

import {
  getWorkDiaryAction,
  setDeleteTaskResponseAction,
  setUpdateWorkDiaryBookingStatusAction,
  setUpdateWorkDiaryResponseAction,
  setWorkDiaryResponseAction,
} from "../../../store/reducers/workDiarySlice";
import Loader from "../../../components/common/loader/Loader";

dayjs.extend(isSameOrBefore);

const CompleteWorkDiary = () => {
  const dispatch = useDispatch();

  const {
    completeWorkDiaryData,
    isCompleteWorkDiaryLoading,
    completeWorkDiarySummary,
    workDiaryData,
    isWorkDiarySuccess,
    isWorkDiaryUpdateSuccess,
    isBookingStatusUpdated,
  } = useSelector((state: any) => state?.workDiary);

  /** -----------------------------
   * DEFAULT WEEK (MONDAY → SUNDAY)
   ------------------------------*/

  const monday = dayjs().startOf("week");
  const sunday = monday.add(6, "day");

  const [filteredData, setFilteredData] = useState({
    search: "",
    startDateRange: monday.format("YYYY-MM-DD"),
    endDateRange: sunday.format("YYYY-MM-DD"),
    taskType: "",
    actionType: "professional",
  });

  /** -----------------------------
   * LOAD WORK DIARY
   ------------------------------*/

  useEffect(() => {
    dispatch(
      getWorkDiaryAction({
        startDate: filteredData.startDateRange,
        endDate: filteredData.endDateRange,
      }),
    );

    dispatch(setWorkDiaryResponseAction(false));
    dispatch(setUpdateWorkDiaryResponseAction(false));
  }, [dispatch]);

  useEffect(() => {
    if (
      isWorkDiarySuccess ||
      isWorkDiaryUpdateSuccess ||
      isBookingStatusUpdated
    ) {
      dispatch(
        getWorkDiaryAction({
          startDate: filteredData.startDateRange,
          endDate: filteredData.endDateRange,
        }),
      );
      dispatch(setWorkDiaryResponseAction(false));
      dispatch(setUpdateWorkDiaryResponseAction(false));
      dispatch(setUpdateWorkDiaryBookingStatusAction(false));
      dispatch(setDeleteTaskResponseAction(false));
    }
  }, [isWorkDiarySuccess, isWorkDiaryUpdateSuccess, isBookingStatusUpdated]);

  /** -----------------------------
   * GROUP CUSTOM TASKS BY DATE
   ------------------------------*/

  const groupedWorkDiaryTasks = useMemo(() => {
    const grouped: Record<string, any[]> = {};

    workDiaryData?.forEach((task: any) => {
      const dateStr = dayjs(task.date).format("YYYY-MM-DD");

      if (!grouped[dateStr]) grouped[dateStr] = [];

      grouped[dateStr].push(task);
    });

    return grouped;
  }, [workDiaryData]);

  /** -----------------------------
   * GENERATE DATE RANGE
   ------------------------------*/

  const getDatesBetween = (start: string, end: string) => {
    const result: any[] = [];

    let current = dayjs(start);
    const last = dayjs(end);

    while (current.isSameOrBefore(last, "day")) {
      const dateStr = current.format("YYYY-MM-DD");

      // const completeData = completeWorkDiaryData?.jobList?.find(
      //   (d: any) => dayjs.utc(d.date).format("YYYY-MM-DD") === dateStr
      // );
      const completeData = completeWorkDiaryData?.find(
        (d: any) => dayjs.utc(d.date).format("YYYY-MM-DD") === dateStr,
      );
      const workTasks = groupedWorkDiaryTasks[dateStr] || [];

      const allTasks = [
        ...(completeData?.tasks || []),
        ...workTasks.map((task: any) => ({
          job_id: null,
          task_id: task.task_id,
          job_title: task?.type ?? "Task",
          customer_name: task.title,
          start_date_time: `${dayjs(task.date).format("YYYY-MM-DD")}T${task.start_time}`,
          end_date_time: `${dayjs(task.date).format("YYYY-MM-DD")}T${task.end_time}`,
          note: task.description,
          status: task.status,
          job_type: "custom",
          location: task.location,
        })),
      ];

      result.push({
        date: dateStr,
        isBooked: completeData?.isBooked || false,
        confirmedTotal: completeData?.confirmedTotal || 0,
        provisionalTotal: completeData?.provisionalTotal || 0,
        totalVolume: completeData?.totalVolume || "0",
        totalMileage: completeData?.totalMileage || "0",
        tasks: allTasks,
      });

      current = current.add(1, "day");
    }

    return result;
  };

  useEffect(() => {
    dispatch(
      getWorkDiaryAction({
        startDate: filteredData.startDateRange,
        endDate: filteredData.endDateRange,
      }),
    );
  }, [filteredData]);

  /** -----------------------------
   * FINAL DATA
   ------------------------------*/

  const finalDiaryData = useMemo(() => {
    return getDatesBetween(
      filteredData.startDateRange,
      filteredData.endDateRange,
    );
  }, [completeWorkDiaryData, groupedWorkDiaryTasks, filteredData]);

  /** -----------------------------
   * RENDER
   ------------------------------*/

  // Ref for horizontal scroll container
  const scrollRef = useRef<HTMLDivElement>(null);

  // Scroll by one card width (327px + margin)
  const CARD_WIDTH = 327 + 16; // 327px + mx:0.5 (8px left/right)

  const scrollByCard = (direction: "left" | "right") => {
    if (scrollRef.current) {
      const scrollAmount = direction === "left" ? -CARD_WIDTH : CARD_WIDTH;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  return (
    <>
    {isCompleteWorkDiaryLoading && <Loader />}
    
    <Box p={0.5} sx={{ backgroundColor: "#F5F5F5", maxWidth: "auto" }}>
      <DiaryHeader
        filteredData={filteredData}
        setFilteredData={setFilteredData}
      />
      {/* Info section */}
      <Box
        sx={{
          background: "#fff",
          border: "1px solid #E6E6E6",
          borderRadius: "5px",
          padding: "6px 16px",
          display: "flex",
          alignItems: "center",
          gap: 3,
          fontSize: 15,
          marginBottom: 2,
          overflowX: "auto",
        }}
      >
        <IconButton
          sx={{ backgroundColor: "#ebe9e9", padding: "3px 10px",borderRadius:'5px' }}
          aria-label="scroll left"
          onClick={() => scrollByCard("left")}
          size="small"
        >
          <span style={{ fontSize: 16 }}>Prev</span>
        </IconButton>
        <span style={{ color: "#888" }}>Total Confirmed :</span>
        <span style={{ fontWeight: 500, marginRight: 16 }}>
          £{completeWorkDiarySummary?.confirmedTotal ? Number(completeWorkDiarySummary.confirmedTotal).toFixed(2) : 0}
        </span>
        <span style={{ color: "#888" }}>Total Provisional :</span>
        <span style={{ fontWeight: 500, marginRight: 16 }}>
          £{completeWorkDiarySummary?.provisionalTotal ? Number(completeWorkDiarySummary.provisionalTotal).toFixed(2) : 0}
        </span>
        <span style={{ color: "#888" }}>Total Confirmed Tasks :</span>
        <span style={{ fontWeight: 500, marginRight: 16 }}>
          {completeWorkDiarySummary?.confirmedTaskCount}
        </span>
        <span style={{ color: "#888" }}>Total Provisional Tasks :</span>
        <span style={{ fontWeight: 500, marginRight: 16 }}>
          {completeWorkDiarySummary?.provisionalTaskCount}
        </span>
        <span style={{ color: "#888" }}>Total Volume :</span>
        <span style={{ fontWeight: 500 }}>
          {completeWorkDiarySummary?.totalVolume ? Number(completeWorkDiarySummary.totalVolume).toFixed(2) : 0}m³
        </span>
        <Box flex={1} />
        <IconButton
          aria-label="scroll right"
          sx={{ backgroundColor: "#ebe9e9", padding: "3px 10px",borderRadius:'5px' }}
          onClick={() => scrollByCard("right")}
          size="small"
        >
          <span style={{ fontSize: 16 }}>Next</span>
        </IconButton>
      </Box>

      <Box
        display="flex"
        overflow="auto"
        pb={2}
        ref={scrollRef}
        sx={{ scrollBehavior: "smooth" }}
      >
        {finalDiaryData.map((day: any, idx: number) => (
          <DayCard key={idx} {...day} />
        ))}
      </Box>
    </Box>
    </>
  );
};

export default CompleteWorkDiary;
